import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import UserManagement from "@/components/UserManagement";
import CategoryManagement from "@/components/CategoryManagement";
import { Megaphone, Calendar, Check, BookOpen } from "lucide-react";

export default function AdminDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [intentions, setIntentions] = useState([]);
    const [pending, setPending] = useState([]);
    const [announcingId, setAnnouncingId] = useState(null);
    const [msg, setMsg] = useState("");

    const todayStr = new Date().toISOString().slice(0, 10);
    const [lit, setLit] = useState({
        date: todayStr, feast: "", word_of_god: "", first_reading: "",
        psalm: "", acclamation: "", gospel: "", note: "",
    });

    const load = async () => {
        const [s, i, p] = await Promise.all([
            api.get("/admin/stats"),
            api.get("/intentions"),
            api.get("/testimonies/pending"),
        ]);
        setStats(s.data);
        setIntentions(i.data);
        setPending(p.data);
    };

    useEffect(() => { load(); }, []);

    const announce = async (id) => {
        await api.post("/admin/announce", { intention_id: id, message: msg || null });
        setAnnouncingId(null);
        setMsg("");
    };

    const approve = async (id) => {
        await api.post(`/testimonies/${id}/approve`);
        setPending((p) => p.filter((x) => x.id !== id));
    };

    const saveLiturgy = async (e) => {
        e.preventDefault();
        await api.post("/liturgical", lit);
        alert("Liturgical reading saved.");
    };

    return (
        <div className="parchment min-h-[calc(100vh-4rem)]">
            <div className="mx-auto max-w-7xl px-6 md:px-10 py-12">
                <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>Moderator</div>
                <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>Chief Consultant’s Desk</h1>

                {stats && (
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { l: "Members", v: stats.users },
                            { l: "Warriors", v: stats.warriors },
                            { l: "Active intentions", v: stats.intentions_active },
                            { l: "Testimonies", v: stats.testimonies },
                        ].map((s, i) => (
                            <div key={i} className="p-card p-5">
                                <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>{s.l}</div>
                                <div className="font-display text-4xl mt-1" style={{ color: "#1E1B4B" }}>{s.v}</div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-10 grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7 p-card p-6">
                        <div className="flex items-center gap-2">
                            <Megaphone size={16} style={{ color: "#7F1D1D" }} />
                            <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>Announce intentions</div>
                        </div>
                        <div className="mt-4 divide-y" style={{ borderColor: "#E5E0D8" }}>
                            {intentions.slice(0, 8).map((i) => (
                                <div key={i.id} className="py-3" data-testid={`admin-intention-${i.id}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-display text-lg truncate" style={{ color: "#1E1B4B" }}>{i.title}</div>
                                            <div className="text-xs" style={{ color: "#525252" }}>{i.requester_name} · {i.prayer_count} praying</div>
                                        </div>
                                        <button className="btn-primary text-xs" onClick={() => setAnnouncingId(announcingId === i.id ? null : i.id)} data-testid={`admin-announce-${i.id}`}>
                                            {announcingId === i.id ? "Cancel" : "Announce"}
                                        </button>
                                    </div>
                                    {announcingId === i.id && (
                                        <div className="mt-3 flex gap-2">
                                            <input placeholder="Optional message (leave blank for default)"
                                                   value={msg} onChange={(e) => setMsg(e.target.value)}
                                                   className="flex-1 px-4 py-2 rounded-full border"
                                                   style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                                   data-testid={`admin-announce-msg-${i.id}`} />
                                            <button onClick={() => announce(i.id)} className="btn-gold text-xs" data-testid={`admin-announce-send-${i.id}`}>Send</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 p-card p-6">
                        <div className="flex items-center gap-2">
                            <Check size={16} style={{ color: "#7F1D1D" }} />
                            <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>Pending testimonies</div>
                        </div>
                        <div className="mt-4 space-y-3">
                            {pending.length === 0 && <div className="text-sm text-neutral-500">All clear.</div>}
                            {pending.map((t) => (
                                <div key={t.id} className="border-b pb-3" style={{ borderColor: "#E5E0D8" }} data-testid={`admin-pending-${t.id}`}>
                                    <div className="font-display text-lg" style={{ color: "#1E1B4B" }}>{t.title}</div>
                                    <div className="text-sm" style={{ color: "#525252" }}>{t.message}</div>
                                    <div className="text-xs mt-1" style={{ color: "#525252" }}>— {t.author_name}</div>
                                    <button onClick={() => approve(t.id)} className="mt-2 btn-gold text-xs" data-testid={`admin-approve-${t.id}`}>Approve</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-10 p-card p-6">
                    <div className="flex items-center gap-2">
                        <BookOpen size={16} style={{ color: "#7F1D1D" }} />
                        <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>Daily Liturgical Reading</div>
                    </div>
                    <form onSubmit={saveLiturgy} className="mt-4 grid gap-3" data-testid="admin-liturgy-form">
                        <div className="flex gap-3 flex-wrap">
                            <input type="date" value={lit.date} onChange={(e) => setLit({ ...lit, date: e.target.value })}
                                   className="px-4 py-2.5 rounded-full border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                   data-testid="admin-lit-date" />
                            <input placeholder="Feast (optional)" value={lit.feast}
                                   onChange={(e) => setLit({ ...lit, feast: e.target.value })}
                                   className="flex-1 px-4 py-2.5 rounded-full border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                   data-testid="admin-lit-feast" />
                        </div>
                        <textarea placeholder="Word of God (key verse)" rows={2} value={lit.word_of_god}
                                  onChange={(e) => setLit({ ...lit, word_of_god: e.target.value })}
                                  className="px-4 py-3 rounded-2xl border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                  data-testid="admin-lit-word" />
                        <textarea placeholder="First Reading" rows={3} value={lit.first_reading}
                                  onChange={(e) => setLit({ ...lit, first_reading: e.target.value })}
                                  className="px-4 py-3 rounded-2xl border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                  data-testid="admin-lit-first" />
                        <textarea placeholder="Psalm" rows={2} value={lit.psalm}
                                  onChange={(e) => setLit({ ...lit, psalm: e.target.value })}
                                  className="px-4 py-3 rounded-2xl border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                  data-testid="admin-lit-psalm" />
                        <textarea placeholder="Acclamation" rows={2} value={lit.acclamation}
                                  onChange={(e) => setLit({ ...lit, acclamation: e.target.value })}
                                  className="px-4 py-3 rounded-2xl border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                  data-testid="admin-lit-accl" />
                        <textarea placeholder="Gospel" rows={3} value={lit.gospel}
                                  onChange={(e) => setLit({ ...lit, gospel: e.target.value })}
                                  className="px-4 py-3 rounded-2xl border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                  data-testid="admin-lit-gospel" />
                        <textarea placeholder="A note for this feast (optional)" rows={2} value={lit.note}
                                  onChange={(e) => setLit({ ...lit, note: e.target.value })}
                                  className="px-4 py-3 rounded-2xl border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                  data-testid="admin-lit-note" />
                        <button type="submit" className="btn-primary w-fit" data-testid="admin-lit-submit">
                            <Calendar size={14} className="inline mr-2" /> Save reading
                        </button>
                    </form>
                </div>

                <div className="mt-10">
                    <CategoryManagement />
                </div>

                <div className="mt-10">
                    <UserManagement currentUserId={user?.id} />
                </div>
            </div>
        </div>
    );
}
